import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  CompanyRequest,
  CompanyResponse,
  FiscalYearRequest,
  FiscalYearResponse,
  WorkCalendarRequest,
  WorkCalendarResponse,
} from '@/types/company';

export const companiesApi = {
  list: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<CompanyResponse>>>('/v1/companies', { params })),

  listActive: () => unwrap(api.get<ApiResponse<CompanyResponse[]>>('/v1/companies/active')),

  /** Companies the current user may work in (header switcher). */
  listAccessible: () => unwrap(api.get<ApiResponse<CompanyResponse[]>>('/v1/companies/accessible')),

  getDefault: () => unwrap(api.get<ApiResponse<CompanyResponse>>('/v1/companies/default')),

  get: (id: number) => unwrap(api.get<ApiResponse<CompanyResponse>>(`/v1/companies/${id}`)),

  create: (body: CompanyRequest) =>
    unwrap(api.post<ApiResponse<CompanyResponse>>('/v1/companies', body)),

  update: (id: number, body: CompanyRequest) =>
    unwrap(api.put<ApiResponse<CompanyResponse>>(`/v1/companies/${id}`, body)),

  delete: (id: number) => unwrap(api.delete<ApiResponse<null>>(`/v1/companies/${id}`)),

  getWorkCalendar: (id: number) =>
    unwrap(api.get<ApiResponse<WorkCalendarResponse>>(`/v1/companies/${id}/work-calendar`)),

  updateWorkCalendar: (id: number, body: WorkCalendarRequest) =>
    unwrap(api.put<ApiResponse<WorkCalendarResponse>>(`/v1/companies/${id}/work-calendar`, body)),

  listHolidayTypes: (companyId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrHolidayType[]>>(`/v1/companies/${companyId}/holiday-types`)),

  createHolidayType: (
    companyId: number,
    body: {
      code: string;
      name: string;
      description?: string;
      nonWorkingDay?: boolean;
      sortOrder?: number;
      active?: boolean;
    },
  ) =>
    unwrap(
      api.post<ApiResponse<import('@/types/hr').HrHolidayType>>(
        `/v1/companies/${companyId}/holiday-types`,
        body,
      ),
    ),

  updateHolidayType: (
    companyId: number,
    typeId: number,
    body: {
      code: string;
      name: string;
      description?: string;
      nonWorkingDay?: boolean;
      sortOrder?: number;
      active?: boolean;
    },
  ) =>
    unwrap(
      api.put<ApiResponse<import('@/types/hr').HrHolidayType>>(
        `/v1/companies/${companyId}/holiday-types/${typeId}`,
        body,
      ),
    ),

  deactivateHolidayType: (companyId: number, typeId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/companies/${companyId}/holiday-types/${typeId}`)),

  listWeekPatterns: (companyId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrWeekPattern[]>>(`/v1/companies/${companyId}/week-patterns`)),

  createWeekPattern: (companyId: number, body: import('@/types/hr').HrWeekPatternForm) =>
    unwrap(
      api.post<ApiResponse<import('@/types/hr').HrWeekPattern>>(
        `/v1/companies/${companyId}/week-patterns`,
        body,
      ),
    ),

  updateWeekPattern: (
    companyId: number,
    patternId: number,
    body: import('@/types/hr').HrWeekPatternForm,
  ) =>
    unwrap(
      api.put<ApiResponse<import('@/types/hr').HrWeekPattern>>(
        `/v1/companies/${companyId}/week-patterns/${patternId}`,
        body,
      ),
    ),

  deactivateWeekPattern: (companyId: number, patternId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/companies/${companyId}/week-patterns/${patternId}`)),

  listFiscalYears: (id: number) =>
    unwrap(api.get<ApiResponse<FiscalYearResponse[]>>(`/v1/companies/${id}/fiscal-years`)),

  createFiscalYear: (id: number, body: FiscalYearRequest) =>
    unwrap(api.post<ApiResponse<FiscalYearResponse>>(`/v1/companies/${id}/fiscal-years`, body)),

  setupStatus: () => unwrap(api.get<ApiResponse<CompanySetupStatus>>('/v1/companies/setup-status')),

  listDepartments: (companyId: number) =>
    unwrap(api.get<ApiResponse<CompanyDepartment[]>>(`/v1/companies/${companyId}/departments`)),

  getDepartmentAccess: (companyId: number, userId: number) =>
    unwrap(
      api.get<ApiResponse<DepartmentAccessResponse>>(
        `/v1/companies/${companyId}/department-access/${userId}`,
      ),
    ),

  saveDepartmentAccess: (companyId: number, body: DepartmentAccessRequest) =>
    unwrap(
      api.put<ApiResponse<DepartmentAccessResponse>>(
        `/v1/companies/${companyId}/department-access`,
        body,
      ),
    ),

  listUserAccessForCompany: (companyId: number) =>
    unwrap(api.get<ApiResponse<UserCompanyAccessMapping[]>>(`/v1/companies/${companyId}/user-access`)),

  listUserCompanyAccess: (userId: number) =>
    unwrap(api.get<ApiResponse<UserCompanyAccessMapping[]>>(`/v1/users/${userId}/company-access`)),

  grantUserCompanyAccess: (userId: number, body: UserCompanyAccessGrantRequest) =>
    unwrap(
      api.post<ApiResponse<UserCompanyAccessMapping>>(`/v1/users/${userId}/company-access`, body),
    ),

  revokeUserCompanyAccess: (userId: number, companyId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/users/${userId}/company-access/${companyId}`)),

  setDefaultUserCompany: (userId: number, companyId: number) =>
    unwrap(
      api.put<ApiResponse<null>>(`/v1/users/${userId}/company-access/default`, { companyId }),
    ),
};

export interface CompanyDepartment {
  id: number;
  name: string;
  code: string;
}

export interface DepartmentAccessRequest {
  userId: number;
  departmentIds: number[];
}

export interface DepartmentAccessResponse {
  userId: number;
  companyId: number;
  departmentIds: number[];
  unrestricted: boolean;
}

export interface UserCompanyAccessMapping {
  id: number;
  userId: number;
  companyId: number;
  companyName?: string;
  companyCode?: string;
  isDefault?: boolean;
  active?: boolean;
}

export interface UserCompanyAccessGrantRequest {
  companyId: number;
  isDefault?: boolean;
}

export interface CompanySetupStatus {
  organizationReady: boolean;
  companyCount: number;
  storeCount: number;
  warehouseCount: number;
  calendarConfigured: boolean;
  hrConfigured: boolean;
  financeConfigured: boolean;
  readyForGoLive: boolean;
  pendingSteps: string[];
}
